import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  Factory, 
  CheckCircle2, 
  XCircle, 
  Clock,
  AlertTriangle,
  Pause,
  Play,
  Wrench,
  Settings,
  RefreshCw,
  CloudSun,
  Droplets,
  Wind,
  Thermometer,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_75f76eb1-ed0f-4a2e-8af9-a573c953ed2b/artifacts/jcb4nruu_7d3b96f2-f08e-453b-8c2c-591199068bb4.jpeg";

const STATUS_CONFIG = {
  running: { label: "EM EXECUÇÃO", color: "bg-green-500", borderColor: "border-green-500", icon: Play },
  idle: { label: "PARADA", color: "bg-amber-500", borderColor: "border-amber-500", icon: Pause },
  stopped: { label: "DESLIGADA", color: "bg-red-500", borderColor: "border-red-500", icon: XCircle },
  maintenance: { label: "MANUTENÇÃO", color: "bg-purple-500", borderColor: "border-purple-500", icon: Wrench },
  setup: { label: "SETUP", color: "bg-blue-500", borderColor: "border-blue-500", icon: Settings },
};

function formatTime(seconds) {
  if (!seconds || seconds <= 0) return "00:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function MachineCard({ data }) {
  const { machine, active_order, part, mold, operator, production, quality, stops } = data;
  const statusConfig = STATUS_CONFIG[machine.status] || STATUS_CONFIG.idle;
  const StatusIcon = statusConfig.icon;
  const progress = active_order ? Math.min(100, (production.good_quantity / active_order.good_quantity_target) * 100) : 0;
  const hasAlert = quality?.rejection_rate > 5 || quality?.blocked;

  return (
    <div className={`bg-white rounded-xl shadow-lg border-2 ${statusConfig.borderColor} overflow-hidden transition-all duration-300 hover:shadow-xl`} data-testid={`factory-machine-${machine.code}`}>
      <div className={`${statusConfig.color} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <StatusIcon className="h-6 w-6 text-white" />
          <span className="text-white font-bold text-xl">{machine.code}</span>
        </div>
        <span className="text-white text-sm font-semibold uppercase tracking-wide">{statusConfig.label}</span>
      </div>
      <div className="p-4 space-y-4">
        <div className="text-center border-b border-gray-100 pb-3">
          <p className="text-gray-600 text-sm">{machine.description}</p>
          <p className="text-gray-400 text-xs">{machine.tonnage}T</p>
        </div>
        {active_order ? (
          <>
            {hasAlert && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <span className="text-red-700 text-sm font-medium">{quality?.blocked ? "BLOQUEADA - QUALIDADE" : `Rejeição: ${quality?.rejection_rate}%`}</span>
              </div>
            )}
            {stops?.active_stop && (
              <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-2 animate-pulse">
                <div className="flex items-center gap-2">
                  <Pause className="h-5 w-5 text-orange-600" />
                  <div>
                    <span className="text-orange-800 text-sm font-bold">PARAGEM ATIVA</span>
                    <p className="text-orange-600 text-xs font-medium">{stops.active_stop.reason || "Sem motivo"}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Ordem Ativa</span>
                <span className="font-mono font-bold text-lg text-gray-800">{active_order.code}</span>
              </div>
              {part && <p className="text-gray-700 font-medium">{part.code} - {part.description}</p>}
              {mold && <p className="text-gray-500 text-sm mt-1">Molde: {mold.code} | {mold.cavities} cav. | {mold.cycle_time}s</p>}
              {operator && <p className="text-blue-600 text-sm mt-2 font-medium">Operador: {operator.name}</p>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
                <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto mb-1" />
                <p className="font-mono text-2xl font-bold text-green-600">{production.good_quantity.toLocaleString()}</p>
                <p className="text-xs text-green-700">BOA</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
                <XCircle className="h-5 w-5 text-red-500 mx-auto mb-1" />
                <p className="font-mono text-2xl font-bold text-red-600">{production.rejected_quantity.toLocaleString()}</p>
                <p className="text-xs text-red-700">REJEITADA</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
                <Clock className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                <p className="font-mono text-2xl font-bold text-blue-600">{production.remaining.toLocaleString()}</p>
                <p className="text-xs text-blue-700">FALTA</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Progresso</span>
                <span className="font-mono font-bold text-gray-800">{progress.toFixed(1)}%</span>
              </div>
              <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full transition-all duration-500 ${progress >= 100 ? 'bg-green-500' : progress >= 75 ? 'bg-blue-500' : progress >= 50 ? 'bg-amber-500' : 'bg-gray-400'}`} style={{ width: `${Math.min(progress, 100)}%` }} />
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>Objetivo: {active_order.good_quantity_target.toLocaleString()}</span>
                <span>MP: {production.raw_material_consumed}kg</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">Decorrido</p>
                <p className="font-mono text-xl font-bold text-gray-700">{formatTime(production.elapsed_time)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">Restante</p>
                <p className="font-mono text-xl font-bold text-blue-600">{formatTime(production.remaining_time)}</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-2 flex items-center justify-between text-sm">
              <span className="text-gray-500">Ritmo:</span>
              <span className={`font-mono font-bold ${production.current_rate >= production.theoretical_rate * 0.9 ? 'text-green-600' : production.current_rate >= production.theoretical_rate * 0.7 ? 'text-amber-600' : 'text-red-600'}`}>
                {production.current_rate} / {production.theoretical_rate} pc/h
              </span>
            </div>
            {stops?.total_time > 0 && (
              <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-amber-700">Paragens:</span>
                  <span className="font-mono font-bold text-amber-700">{stops.total_count || 0}x | {stops.total_time} min</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-8 text-center">
            <Factory className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg font-medium">Sem ordem ativa</p>
            <p className="text-gray-400 text-sm">Aguardando atribuição</p>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Weather Widget (compact inline) ---
function WeatherWidget() {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const r = await fetch("https://api.open-meteo.com/v1/forecast?latitude=39.24&longitude=-8.69&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&daily=temperature_2m_max,temperature_2m_min&timezone=Europe/Lisbon&forecast_days=1");
        setWeather(await r.json());
      } catch (e) {}
    };
    fetchWeather();
    const iv = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  const getDesc = (code) => {
    if (!code && code !== 0) return "";
    if (code === 0) return "Ceu limpo";
    if (code <= 3) return "Parc. nublado";
    if (code <= 49) return "Nevoeiro";
    if (code <= 69) return "Chuva";
    if (code <= 79) return "Neve";
    if (code <= 82) return "Aguaceiros";
    return "Trovoada";
  };

  if (!weather?.current) return null;

  return (
    <div className="flex items-center gap-3 text-white text-sm">
      <CloudSun className="h-5 w-5 text-amber-300" />
      <span className="font-bold text-base">{weather.current.temperature_2m}°C</span>
      <span className="text-amber-200">Santarem</span>
      <span className="text-slate-400">|</span>
      <span className="text-slate-300 flex items-center gap-1"><Droplets className="h-3.5 w-3.5" />{weather.current.relative_humidity_2m}%</span>
      <span className="text-slate-300 flex items-center gap-1"><Wind className="h-3.5 w-3.5" />{weather.current.wind_speed_10m}km/h</span>
      <span className="text-slate-300">{getDesc(weather.current.weather_code)}</span>
      {weather.daily && <span className="text-slate-400 flex items-center gap-1"><Thermometer className="h-3.5 w-3.5" />{weather.daily.temperature_2m_min[0]}°/{weather.daily.temperature_2m_max[0]}°</span>}
    </div>
  );
}

// --- News Ticker (horizontal scrolling like TV) ---
function NewsTicker() {
  const [news, setNews] = useState([]);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const r = await axios.get(`${API}/shop-floor/news`);
        setNews(r.data);
      } catch (e) {}
    };
    fetchNews();
    const iv = setInterval(fetchNews, 15 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  if (news.length === 0) return <p className="text-slate-500 text-sm animate-pulse">A carregar noticias...</p>;

  const tickerText = news.map(a => a.title).join("    ///    ");

  return (
    <div className="overflow-hidden whitespace-nowrap">
      <div className="inline-block animate-ticker">
        <span className="text-white text-lg font-bold">{tickerText}    ///    {tickerText}</span>
      </div>
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-ticker {
          animation: ticker 120s linear infinite;
        }
      `}</style>
    </div>
  );
}

// --- Next Orders Table ---
function NextOrdersTable() {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const r = await axios.get(`${API}/shop-floor/next-orders`);
        setOrders(r.data);
      } catch (e) {}
    };
    fetchOrders();
    const iv = setInterval(fetchOrders, 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  const statusLabels = { planned: "PLANEADA", pending: "PENDENTE", ready: "PRONTA" };
  const statusColors = { planned: "bg-blue-500", pending: "bg-amber-500", ready: "bg-green-500" };

  if (orders.length === 0) return null;

  return (
    <div className="bg-slate-900 rounded-t-xl overflow-hidden shadow-2xl">
      {/* Table Header */}
      <div className="bg-amber-500 px-4 py-2 flex items-center justify-between">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Proximas Ordens de Producao
        </h3>
        <span className="text-amber-100 text-xs">{orders.length} ordens em fila</span>
      </div>
      {/* Table */}
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-800 text-slate-400 text-xs uppercase tracking-wider">
            <th className="text-left px-4 py-2 font-semibold">Estado</th>
            <th className="text-left px-4 py-2 font-semibold">Ordem</th>
            <th className="text-left px-4 py-2 font-semibold">Peca</th>
            <th className="text-left px-4 py-2 font-semibold">Descricao</th>
            <th className="text-center px-4 py-2 font-semibold">Maquina</th>
            <th className="text-right px-4 py-2 font-semibold">Quantidade</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order, i) => (
            <tr key={i} className={`border-t border-slate-800 ${i % 2 === 0 ? 'bg-slate-900' : 'bg-slate-850'} hover:bg-slate-800 transition-colors`}>
              <td className="px-4 py-2">
                <span className={`${statusColors[order.status] || "bg-gray-500"} text-white text-xs font-bold px-2 py-0.5 rounded-full uppercase`}>
                  {statusLabels[order.status] || order.status}
                </span>
              </td>
              <td className="px-4 py-2 font-mono font-bold text-amber-400">{order.code}</td>
              <td className="px-4 py-2 text-white font-medium">{order.part_code}</td>
              <td className="px-4 py-2 text-slate-300 truncate max-w-[200px]">{order.part_description}</td>
              <td className="px-4 py-2 text-center">
                <span className="bg-slate-700 text-slate-200 text-xs font-mono font-bold px-2 py-1 rounded">{order.machine_code}</span>
              </td>
              <td className="px-4 py-2 text-right font-mono font-bold text-white">{order.total_quantity ? order.total_quantity.toLocaleString() : "-"} <span className="text-slate-500 text-xs">un</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ShopFloor2() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/factory`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 30 * 1000);
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => { clearInterval(interval); clearInterval(clockInterval); };
  }, [fetchDashboard]);

  const runningMachines = dashboardData?.machines?.filter(m => m.machine.status === "running").length || 0;
  const totalMachines = dashboardData?.machines?.length || 0;
  const totalGoodProduction = dashboardData?.machines?.reduce((sum, m) => sum + (m.production?.good_quantity || 0), 0) || 0;
  const totalRejected = dashboardData?.machines?.reduce((sum, m) => sum + (m.production?.rejected_quantity || 0), 0) || 0;
  const qualityRate = totalGoodProduction + totalRejected > 0 ? ((totalGoodProduction / (totalGoodProduction + totalRejected)) * 100).toFixed(1) : "100";

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-16 w-16 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600 text-xl">A carregar dados da fabrica...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex flex-col" data-testid="shop-floor-display">
      {/* Header Bar */}
      <header className="bg-white shadow-md px-6 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Logo" className="h-12 w-auto" />
            <div>
              <h1 className="font-bold text-2xl text-gray-800">Painel da Fabrica</h1>
              <p className="text-gray-500 text-sm">Monitorizacao em Tempo Real</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center px-4 py-2 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-600 uppercase font-medium">Maquinas Ativas</p>
              <p className="text-3xl font-bold text-green-700">{runningMachines}/{totalMachines}</p>
            </div>
            <div className="text-center px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-600 uppercase font-medium">Producao Total</p>
              <p className="text-3xl font-bold text-blue-700">{totalGoodProduction.toLocaleString()}</p>
            </div>
            <div className="text-center px-4 py-2 bg-red-50 rounded-lg border border-red-200">
              <p className="text-xs text-red-600 uppercase font-medium">Rejeitadas</p>
              <p className="text-3xl font-bold text-red-700">{totalRejected.toLocaleString()}</p>
            </div>
            <div className="text-center px-4 py-2 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-600 uppercase font-medium">Taxa Qualidade</p>
              <p className="text-3xl font-bold text-purple-700">{qualityRate}%</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-mono text-4xl font-bold text-gray-800">{currentTime.toLocaleTimeString("pt-PT")}</p>
            <p className="text-gray-500 text-sm">{currentTime.toLocaleDateString("pt-PT", { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
        </div>
      </header>

      {/* Machines Grid */}
      <main className="flex-1 p-6 overflow-auto">
        {dashboardData?.machines?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {dashboardData.machines.map((machineData) => (
              <MachineCard key={machineData.machine.id} data={machineData} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <Factory className="h-24 w-24 mx-auto mb-6 text-gray-300" />
            <p className="text-gray-500 text-2xl font-medium">Nenhuma maquina configurada</p>
            <p className="text-gray-400 mt-2">Configure as maquinas na area de administracao</p>
          </div>
        )}
      </main>

      {/* Bottom Section: Orders Table + TV + Weather */}
      <div className="shrink-0 px-6 pb-0">
        {/* Orders + TV side by side */}
        <div className="grid grid-cols-2 gap-0" style={{ height: "45vh" }}>
          {/* Left: Next Orders Table */}
          <div className="overflow-hidden flex flex-col">
            <NextOrdersTable />
          </div>

          {/* Right: TV Player */}
          <div className="bg-black rounded-tr-xl overflow-hidden flex flex-col">
            <div className="bg-amber-500 px-3 py-1.5 flex items-center justify-between shrink-0">
              <span className="text-white font-bold text-xs uppercase tracking-wider flex items-center gap-2">
                <span className="h-2 w-2 bg-white rounded-full animate-pulse" />
                Euronews PT - Em Direto
              </span>
            </div>
            <div className="flex-1 relative">
              <iframe
                src="https://www.youtube.com/embed/live_stream?channel=UCUmEPYxmnyQDeRUcFkslmQw&autoplay=1&mute=1&rel=0&modestbranding=1"
                title="Euronews Em Direto"
                className="absolute inset-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        {/* Weather Bar - full width */}
        <div className="bg-slate-900 flex items-center justify-between px-6 py-2">
          <WeatherWidget />
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <RefreshCw className="h-3 w-3" />
            <span>Auto-refresh 30s</span>
            <span className="text-slate-700 mx-2">|</span>
            <span>EP3D ENGINEERING PROJECT</span>
            <span className="text-slate-700 mx-2">|</span>
            <span>Atualizado: {dashboardData?.timestamp ? new Date(dashboardData.timestamp).toLocaleTimeString("pt-PT") : "-"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
